import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faArrowRight,
	faCheckCircle,
	faCircle,
	faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons"
import {
	corpsDelta,
	fetchDispatchEnCours,
	putDepannageDispatch,
	putDepannageDispatchReset,
} from "../../../store/actions/depannageDispatchActions"
import {
	DEP_STATE_CANCELED,
	DEP_STATE_DISPATCHED,
	DEP_STATE_READY_TO_DISPATCH,
	DEP_STATES,
} from "../../../utilities/Addons"
import { filterEmployesGroupe } from "../../../utilities/employes"
import SelectV2 from "../../basics/input/SelectV2"
import AppBtn from "../../basics/button/AppBtn"
import { COLORS } from "../../../utilities/Constants"
import { getLieuAffichage } from "../../../utilities/depLieux"
import CustomModal from "../../basics/interface/CustomModal"
import Loader from "../../basics/Loader"
import { DISPATCH_POLL_MS } from "./DepDispatchATraiter"
import DepMissionCardV2, {
	OuvrirFicheBtn,
	RelivraisonBadge,
	TerrainBadge,
} from "./DepMissionCardV2"
import PhotosMission from "./PhotosMission"
import CompleterMission from "./CompleterMission"
import { abonnerMissionsModifiees } from "../../../utilities/notificationsSocket"

// Étapes de l'intervention, dérivées des timestamps posés par le mobile
// (sémantique 4 jalons, 07.2026 : t2 = départ du lieu, t3 = arrivée).
const ETAPES = [
	{ key: "timestamp_0", label: "Parti" },
	{ key: "timestamp_1", label: "Arrivé sur place" },
	{ key: "timestamp_2", label: "Reparti du lieu" },
	{ key: "timestamp_3", label: "Arrivé à destination" },
]

const heure = (ts) => (ts ? ts.split(" ")[1] : null)

// Mission annulée : le dernier jalon non-null est le moment de l'annulation.
const dernierJalonAnnule = (dm) => {
	const pose = ETAPES.filter((e) => dm[e.key])
	return pose.length > 0 ? pose[pose.length - 1].key : null
}

const statutTemporel = (dm) => {
	if (dm.etat === DEP_STATE_DISPATCHED) return "Pas commencé"
	if (dm.etat === DEP_STATE_CANCELED) {
		const key = dernierJalonAnnule(dm)
		return key ? `Annulé à ${heure(dm[key])}` : "Annulé"
	}
	if (dm.timestamp_3) return `Arrivé à destination à ${heure(dm.timestamp_3)}`
	if (dm.timestamp_2) return `Reparti du lieu à ${heure(dm.timestamp_2)}`
	if (dm.timestamp_1) return `Arrivé sur place à ${heure(dm.timestamp_1)}`
	if (dm.timestamp_0) return `Parti à ${heure(dm.timestamp_0)}`
	return "En cours"
}

// Onglet « En cours » : missions dispatchées (30) et en intervention (50).
// Cartes cliquables → détail en lecture (timeline, lieux, infos dépanneur).
const DepDispatchEnCours = () => {
	const dispatch = useDispatch()
	// Détail relu dans la liste à chaque poll (photos, jalons arrivés
	// entre-temps) ; la copie du clic sert de repli si la mission sort de la
	// liste (annulée, convertie) pendant que le détail est ouvert.
	const [selection, setDetail] = useState(null)
	// Convention modal : switch de confirmation sur le bouton d'annulation.
	const [confirmAnnulation, setConfirmAnnulation] = useState(false)
	// GE-62 (B5) : formulaire « Compléter » à la place du détail.
	const [completer, setCompleter] = useState(false)
	// B1 (CDC 16.10, note 7.25.0) : re-dispatch / retrait depuis le web, dans
	// les limites serveur (versions.garde_en_cours) : re-dispatch vers un
	// AUTRE dépanneur tant qu'il n'est pas arrivé sur place ; retrait (→ Prêt
	// au dispatch) seulement tant que la mission n'est pas partie (30).
	// Confirmation par switch du bouton (convention V2 dans un modal).
	const [nouveauDepanneur, setNouveauDepanneur] = useState(0)
	const [confirmAction, setConfirmAction] = useState(null)
	const { depsEnCours, fetchDepsEnCours, putDepsToDispatch } = useSelector(
		(state) => state.depannageDispatchInfo,
	)
	const { employes: depanneurs } = useSelector((state) => state.masterReducer)
	const busy = putDepsToDispatch.saving || putDepsToDispatch.saved
	// Revue phase B (🟠4) : la DERNIÈRE copie vue (jamais celle du clic) ;
	// sortie de la liste → bandeau, Compléter en lecture seule.
	const [derniere, setDerniere] = useState(null)
	const etatsVus = useRef({})
	const enListe = selection
		? depsEnCours.find((dm) => dm.id === selection.id)
		: null
	useEffect(() => {
		if (!enListe) return
		setDerniere((avant) =>
			avant &&
			avant.id === enListe.id &&
			(avant.version || 0) > (enListe.version || 0)
				? avant
				: enListe,
		)
	}, [enListe])
	// État annoncé par le temps réel (la liste ne dit plus rien d'une
	// mission sortie d'En cours).
	useEffect(
		() =>
			abonnerMissionsModifiees((data) => {
				etatsVus.current[data.id] = data.etat
			}),
		[],
	)
	const detail = selection
		? enListe ||
			(derniere && derniere.id === selection.id ? derniere : selection)
		: null
	// Revue 5 : sans condition sur le chargement (repassait à vrai à chaque
	// sondage / événement → le bandeau clignotait ; la liste est gardée
	// pendant le chargement et sur erreur).
	const sortie =
		!!selection && !enListe
			? (etatsVus.current[selection.id] ?? true)
			: false
	// L'état n'est dit que s'il est hors d'En cours (30 / 40 / 50).
	const etatSortie =
		typeof sortie === "number" && ![30, 40, 50].includes(sortie)
			? sortie
			: null
	const bandeauSortie =
		sortie === false
			? null
			: `Mission sortie d'En cours${
					etatSortie !== null
						? ` (état « ${DEP_STATES[etatSortie] || etatSortie} »)`
						: ""
				} : affichage de la dernière version vue, plus de modification ici.`

	const getDepanneur = (dm) => {
		const d = depanneurs.find((dep) => dep.id === dm.depanneur)
		return d ? d.nom : "—"
	}

	useEffect(() => {
		// Purge tout état saved/error laissé par un autre onglet (slice partagé).
		dispatch(putDepannageDispatchReset())
		dispatch(fetchDispatchEnCours())
		const interval = setInterval(
			() => dispatch(fetchDispatchEnCours()),
			DISPATCH_POLL_MS,
		)
		return () => clearInterval(interval)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Annulation confirmée : le backend notifie le dépanneur et nettoie la
	// mission (→ onglet Annulées). ✓ 1 s puis fermeture + refresh.
	// Seuls id + état partent : `detail` date du dernier poll (jusqu'à 45 s)
	// et renvoyé en entier, il écrasait ce que le dépanneur venait d'envoyer.
	// Revue 5 : en delta avec la version lue (terminée entre-temps → 409),
	// et jamais sous le bandeau « sortie ».
	const redispatcher = () => {
		if (busy || !detail || bandeauSortie || !nouveauDepanneur) return
		if (confirmAction !== "redispatch") {
			setConfirmAction("redispatch")
			return
		}
		const changes =
			detail.etat === DEP_STATE_DISPATCHED
				? { depanneur: nouveauDepanneur }
				: { etat: DEP_STATE_DISPATCHED, depanneur: nouveauDepanneur }
		dispatch(putDepannageDispatch(corpsDelta(detail, changes)))
	}

	const retirer = () => {
		if (busy || !detail || bandeauSortie) return
		if (confirmAction !== "retrait") {
			setConfirmAction("retrait")
			return
		}
		dispatch(
			putDepannageDispatch(
				corpsDelta(detail, {
					etat: DEP_STATE_READY_TO_DISPATCH,
					depanneur: 0,
				}),
			),
		)
	}

	const annuler = () => {
		if (busy || !detail || bandeauSortie) return
		if (!confirmAnnulation) {
			setConfirmAnnulation(true)
			return
		}
		dispatch(
			putDepannageDispatch(
				corpsDelta(detail, { etat: DEP_STATE_CANCELED }),
			),
		)
	}

	// Revue 6 (D-3) : 409 (mission modifiée entre-temps) → relecture tout de
	// suite, sans attendre le sondage (45 s) si le WS est en panne.
	useEffect(() => {
		const e = putDepsToDispatch.error
		if (e && e.httpStatus === 409) dispatch(fetchDispatchEnCours())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [putDepsToDispatch.error])

	useEffect(() => {
		if (!putDepsToDispatch.saved) return
		const timeout = setTimeout(() => {
			dispatch(putDepannageDispatchReset())
			setDetail(null)
			setConfirmAnnulation(false)
			setConfirmAction(null)
			setNouveauDepanneur(0)
			dispatch(fetchDispatchEnCours())
		}, 1000)
		return () => clearTimeout(timeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [putDepsToDispatch.saved])

	const lieu = detail
		? getLieuAffichage(detail.lieu_intervention, [
				detail.lieu_intervention_0,
				detail.lieu_intervention_1,
				detail.lieu_intervention_2,
			])
		: null
	const destination = detail
		? getLieuAffichage(detail.destination_finale, [
				detail.destination_finale_0,
				detail.destination_finale_1,
				detail.destination_finale_2,
			])
		: null

	return (
		<>
			{detail && completer && (
				<CompleterMission
					dm={detail}
					sortie={bandeauSortie}
					onClose={() => setCompleter(false)}
				/>
			)}
			{detail && !completer && (
				<CustomModal
					title={
						`${detail.marque} ${detail.modele}`.trim() || "Mission"
					}
					subtitle={[detail.plaques, getDepanneur(detail)]
						.filter(Boolean)
						.join(" · ")}
					onClose={
						busy
							? () => {}
							: () => {
									setDetail(null)
									setConfirmAnnulation(false)
									setConfirmAction(null)
									setNouveauDepanneur(0)
								}
					}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Compléter",
							styleName: "secondary",
							onClick: () => {
								setConfirmAnnulation(false)
								setCompleter(true)
							},
							disabled: busy || !!bandeauSortie,
						},
						{
							title: confirmAnnulation
								? "Confirmer l'annulation ?"
								: "Annuler la mission",
							styleName: "danger",
							onClick: annuler,
							disabled: busy || !!bandeauSortie,
						},
						{
							title: "Fermer",
							styleName: "secondary",
							onClick: () => {
								setDetail(null)
								setConfirmAnnulation(false)
								setConfirmAction(null)
								setNouveauDepanneur(0)
							},
							disabled: busy,
						},
					]}
				>
					{bandeauSortie && (
						<div style={st.sortie} role="status">
							{bandeauSortie}
						</div>
					)}
					{!bandeauSortie && !detail.timestamp_1 && (
						<div style={st.redispatch}>
							<div style={st.blocTitre}>Changer de dépanneur</div>
							<div style={st.redispatchRow}>
								<SelectV2
									value={nouveauDepanneur}
									onChange={(e) => {
										setConfirmAction(null)
										setNouveauDepanneur(
											parseInt(e.target.value),
										)
									}}
									disabled={busy}
									style={st.redispatchSelect}
								>
									<option value={0}>Autre dépanneur…</option>
									{filterEmployesGroupe(depanneurs, 0)
										.filter(
											(d) => d.id !== detail.depanneur,
										)
										.map((d) => (
											<option key={d.id} value={d.id}>
												{d.nom}
											</option>
										))}
								</SelectV2>
								<AppBtn
									title={
										confirmAction === "redispatch"
											? "Confirmer le re-dispatch ?"
											: "Re-dispatcher"
									}
									styleName="primary"
									onClick={redispatcher}
									disabled={busy || !nouveauDepanneur}
								/>
								{detail.etat === DEP_STATE_DISPATCHED && (
									<AppBtn
										title={
											confirmAction === "retrait"
												? "Confirmer le retrait ?"
												: "Retirer du dispatch"
										}
										styleName="secondary"
										onClick={retirer}
										disabled={busy}
									/>
								)}
							</div>
							<div style={st.redispatchAide}>
								{detail.etat === DEP_STATE_DISPATCHED
									? "Pas encore partie : le dépanneur actuel est prévenu, la mission repart chez l'autre ou revient « Prêt au dispatch »."
									: "Partie mais pas encore sur place : ses jalons sont effacés, la mission repart à zéro chez l'autre dépanneur."}
							</div>
							{putDepsToDispatch.error && (
								<div style={st.erreurAction}>
									{putDepsToDispatch.error.message ||
										"Action refusée."}
								</div>
							)}
						</div>
					)}
					{detail.dep ? (
						<div style={st.ficheLiee}>
							<RelivraisonBadge dep={detail} />
							<OuvrirFicheBtn ficheId={detail.dep} />
						</div>
					) : null}
					{detail.cree_terrain ? (
						<div style={st.ficheLiee}>
							<TerrainBadge dep={detail} />
						</div>
					) : null}
					{/* GE-67 : fournitures vendues par le dépanneur → fiche
					    atelier « client de passage ». */}
					{detail.fiche_atelier ? (
						<div style={st.ficheLiee}>
							<Link
								to={`/atelier/fiche/${detail.fiche_atelier.id}`}
								style={{
									color: COLORS.accent,
									fontWeight: 600,
								}}
							>
								Fournitures : fiche atelier N°{" "}
								{detail.fiche_atelier.id}
							</Link>
						</div>
					) : null}
					{/* Timeline de l'intervention */}
					<div style={st.timeline}>
						{ETAPES.map((e) => {
							const done = !!detail[e.key]
							return (
								<div key={e.key} style={st.etape}>
									<FontAwesomeIcon
										icon={done ? faCheckCircle : faCircle}
										style={{
											color: done
												? COLORS.success
												: COLORS.border,
										}}
									/>
									<span
										style={{
											fontSize: 13,
											color: done
												? COLORS.text
												: COLORS.textLight,
										}}
									>
										{e.label}
										{done
											? ` — ${heure(detail[e.key])}`
											: ""}
									</span>
								</div>
							)
						})}
					</div>

					{lieu && (
						<div style={st.detailLigne}>
							<a
								href={lieu.mapsUrl}
								target="_blank"
								rel="noopener noreferrer"
								style={{ color: COLORS.accent }}
							>
								<FontAwesomeIcon icon={faMapMarkerAlt} />
							</a>
							<span>{lieu.lines.join(", ")}</span>
						</div>
					)}
					{destination && (
						<div style={st.detailLigne}>
							<a
								href={destination.mapsUrl}
								target="_blank"
								rel="noopener noreferrer"
								style={{ color: COLORS.accent }}
							>
								<FontAwesomeIcon icon={faArrowRight} />
							</a>
							<span>{destination.lines.join(", ")}</span>
						</div>
					)}
					{detail.diagnostique_presume ? (
						<div style={st.bloc}>
							<div style={st.blocTitre}>Diagnostic présumé</div>
							{detail.diagnostique_presume}
						</div>
					) : null}
					{detail.info_depanneur ? (
						<div style={st.bloc}>
							<div style={st.blocTitre}>Info dépanneur</div>
							<span style={{ whiteSpace: "pre-line" }}>
								{detail.info_depanneur}
							</span>
						</div>
					) : null}
					<PhotosMission dm={detail} />
					{putDepsToDispatch.error && (
						<div style={{ fontSize: 13, color: COLORS.danger }}>
							{/* Revue 6 (D-3) : la vraie raison (ex. mission
							    terminée entre-temps), pas un message générique. */}
							{putDepsToDispatch.error.message ||
								"L'annulation a échoué. Réessayez."}
						</div>
					)}
				</CustomModal>
			)}

			{fetchDepsEnCours.loading && depsEnCours.length === 0 ? (
				<Loader variant="ring" message="Chargement…" />
			) : depsEnCours.length === 0 ? (
				<div style={st.empty}>Aucun dépannage en cours</div>
			) : (
				<div style={st.grid}>
					{depsEnCours.map((dm) => (
						<DepMissionCardV2
							key={`en_cours_${dm.id}`}
							dep={dm}
							statut={statutTemporel(dm)}
							onClick={() => {
								setConfirmAnnulation(false)
								setCompleter(false)
								setDetail(dm)
							}}
						/>
					))}
				</div>
			)}
		</>
	)
}

const st = {
	redispatch: {
		marginBottom: 14,
		padding: "10px 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
	},
	redispatchRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		flexWrap: "wrap",
	},
	redispatchSelect: {
		flex: 1,
		minWidth: 200,
		height: 38,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
	},
	redispatchAide: { marginTop: 6, fontSize: 12, color: COLORS.textMuted },
	erreurAction: { marginTop: 6, fontSize: 12.5, color: COLORS.danger },
	grid: {
		display: "flex",
		flexWrap: "wrap",
		gap: 12,
	},
	ficheLiee: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
		marginBottom: 14,
	},
	empty: { padding: "24px 0", fontSize: 13, color: COLORS.textLight },
	sortie: {
		marginBottom: 14,
		padding: "8px 12px",
		borderRadius: 8,
		background: COLORS.warningLight,
		border: `1px solid ${COLORS.warning}`,
		fontSize: 12.5,
		color: COLORS.text,
	},
	timeline: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		marginBottom: 14,
	},
	etape: { display: "flex", alignItems: "center", gap: 10 },
	detailLigne: {
		display: "flex",
		gap: 10,
		alignItems: "flex-start",
		fontSize: 13,
		color: COLORS.text,
		marginBottom: 8,
	},
	bloc: { fontSize: 13, color: COLORS.text, marginBottom: 10 },
	blocTitre: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		marginBottom: 4,
	},
}

export default DepDispatchEnCours
